import { useState } from "react";
import { Button, EmptyState, cn } from "@playbookai/ui";
import type { ManagedKpiDetail } from "../api/types.js";
import { DIRECTION_LABEL, formatDate } from "./kpi-presentation.js";
import { targetProgress } from "./kpi-target-progress.js";
import { KpiPanel } from "./KpiPanel.js";
import { SetTargetModal } from "./SetTargetModal.js";
import { useKpiTtiBowling } from "../hooks/useKpiTtiBowling.js";

export interface KpiTargetSectionProps {
  readonly kpi: ManagedKpiDetail;
  /** Tenant-admin — whether to offer the contextual target action. The server
   * re-checks the realm role on `POST /v1/kpis/:id/targets`. */
  readonly canManage: boolean;
  /**
   * Open this target's Target-to-Improve page (PB-5317). The feature owns no
   * router (ADR-0009), so the host navigates — and without it the link is simply
   * not offered rather than going nowhere.
   */
  readonly onOpenTti?: ((ttiId: string) => void) | undefined;
}

// Kept hand-built (not DescriptionList/KPIValue): every figure here is
// font-mono tabular-nums (a fixed-width KPI reading), and KPIValue's figure
// is font-semibold with no monospace option — a font-family change no T01
// row approves.
function Cell({
  label,
  value,
  muted = false,
}: {
  readonly label: string;
  readonly value: string | null;
  readonly muted?: boolean;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="mb-0.75 text-dense-1 font-semibold uppercase tracking-wider text-pb-text-muted">
        {label}
      </dt>
      <dd
        className={cn(
          "truncate font-mono tabular-nums",
          muted
            ? "text-heading-3 font-medium text-pb-text-muted"
            : "text-heading-2 text-pb-text",
        )}
      >
        {value ?? "—"}
      </dd>
    </div>
  );
}

/**
 * Target to improve (PB-2941, d37) — the standing agreement: a baseline, a
 * target, a date to reach it by, and one derived sentence on where the KPI
 * stands against it. Values are rendered as given — never re-derived from a
 * parsed number, so a decimal the platform stores exactly is shown exactly.
 * The target action is Tenant-Admin only and lives HERE rather than in the
 * page header because "set the target" is a thing you do to the target you
 * are looking at.
 */
export function KpiTargetSection({
  kpi,
  canManage,
  onOpenTti,
}: KpiTargetSectionProps): JSX.Element {
  const [targetOpen, setTargetOpen] = useState(false);
  const target = kpi.currentTarget;
  // Whether this target is also a Hoshin Target to Improve THIS caller may open.
  // The hook answers `null` for both "a standalone KPI target" and "a Hoshin
  // target you cannot view" — deliberately indistinguishable — so a non-null
  // answer is exactly the condition for offering the link, and nobody is shown a
  // door that 404s.
  const ttiSeries = useKpiTtiBowling(target?.ttiId ?? null);
  const ttiId = ttiSeries.data ? target?.ttiId : undefined;
  const withUnit = (value: string | null): string | null =>
    value === null
      ? null
      : kpi.unit
        ? `${value}${kpi.unit === "%" ? "" : " "}${kpi.unit}`
        : value;
  const progress = targetProgress(kpi);

  return (
    <KpiPanel
      title="Target to improve"
      // The card's own name opens the TTI's page when there is one to open —
      // the reader is already looking at the thing the link leads to.
      {...(ttiId && onOpenTti
        ? { onTitleActivate: () => onOpenTti(ttiId) }
        : {})}
      subtitle="Where this KPI is aiming, and by when."
      headingId="kpi-target-heading"
      action={
        canManage ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setTargetOpen(true)}
          >
            {target ? "Edit target" : "Set target"}
          </Button>
        ) : null
      }
    >
      {target ? (
        <>
          <dl className="grid grid-cols-3 gap-3.5">
            <Cell
              label="Baseline"
              value={withUnit(target.baselineValue)}
              muted
            />
            <Cell label="Target" value={withUnit(target.targetValue)} />
            <Cell
              label="Improve by"
              value={target.targetDate ? formatDate(target.targetDate) : null}
              muted
            />
          </dl>
          <p className="mt-3.25 text-caption leading-4.5 text-pb-text-muted">
            {DIRECTION_LABEL[kpi.direction]}.{" "}
            {progress.gap ? (
              <>
                Currently{" "}
                <strong className="font-semibold text-pb-text-secondary">
                  {progress.gap}
                </strong>
                {progress.timeLeft ? ` ${progress.timeLeft}` : ""}.
              </>
            ) : (
              <>
                No value recorded yet
                {progress.timeLeft ? `, ${progress.timeLeft}` : ""}.
              </>
            )}
            {target.note ? ` — ${target.note}` : ""}
          </p>
        </>
      ) : (
        <EmptyState
          variant="first-use"
          title="No target set yet"
          description="A KPI needs an owner and a target to improve before it can be activated."
        />
      )}

      <SetTargetModal
        kpiId={kpi.kpiId}
        target={target}
        open={targetOpen}
        onClose={() => setTargetOpen(false)}
      />
    </KpiPanel>
  );
}
